import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, apiGet } from "../api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Filter, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * Targeted invoice delete: pick any combination of {supplier, month, year}
 * → see a live count preview → confirm → delete.
 *
 * Requires at least one filter (enforced client + server-side).
 * Fires onDeleted(count) after a successful delete so the parent can refresh.
 */
export const InvoiceFilterDelete = ({ onDeleted }) => {
  const [supplier, setSupplier] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [months, setMonths] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load supplier + month pickers (once). Defensive filter: only accept
  // strict YYYY-MM strings so any dirty data in the DB can't leak into the
  // dropdown and produce failed delete calls.
  useEffect(() => {
    apiGet("/suppliers/with-data").then(d => setSuppliers(d?.suppliers || [])).catch(() => {});
    apiGet("/invoices/months").then(d => {
      const clean = (d?.months || []).filter(m => /^\d{4}-\d{2}$/.test(m));
      setMonths(clean);
    }).catch(() => {});
  }, []);

  // Years available — derived from months, with reverse chrono order.
  const years = Array.from(new Set(months.map(m => m.slice(0, 4)))).sort().reverse();

  // Live preview count (debounced) whenever filters change.
  const hasAnyFilter = Boolean(supplier || month || year);

  const fetchPreview = useCallback(async () => {
    if (!hasAnyFilter) {
      setPreview(null);
      return;
    }
    setPreviewing(true);
    try {
      const params = new URLSearchParams();
      if (supplier) params.set("supplier", supplier);
      if (month) params.set("month", month);
      else if (year) params.set("year", year);
      const data = await apiGet(`/invoices/filter-preview?${params}`);
      setPreview(data?.count ?? 0);
    } catch (err) {
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  }, [supplier, month, year, hasAnyFilter]);

  useEffect(() => {
    const t = setTimeout(fetchPreview, 300);
    return () => clearTimeout(t);
  }, [fetchPreview]);

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      const params = new URLSearchParams();
      if (supplier) params.set("supplier", supplier);
      if (month) params.set("month", month);
      else if (year) params.set("year", year);
      const res = await axios.delete(`${API}/invoices/by-filter?${params}`);
      const deleted = res.data?.deleted ?? 0;
      toast.success(`Deleted ${deleted.toLocaleString()} invoice${deleted === 1 ? "" : "s"}`);
      setConfirmOpen(false);
      setSupplier("");
      setMonth("");
      setYear("");
      setPreview(null);
      onDeleted?.(deleted);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to delete invoices";
      toast.error(detail);
    } finally {
      setDeleting(false);
    }
  };

  // Build a human-readable summary of the active filter for the confirm dialog.
  const filterSummary = [
    supplier && `supplier = ${supplier}`,
    month ? `month = ${month}` : year && `year = ${year}`,
  ].filter(Boolean).join(" AND ");

  const previewColor = preview > 0 ? "#F050F8" : "var(--octopus-voltage)";

  return (
    <div className="card" data-testid="invoice-filter-delete-panel" style={{ marginTop: "1.5rem", marginBottom: "1.5rem", border: "1px solid rgba(240, 80, 248, 0.25)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
        <Filter size={16} style={{ color: "#F050F8" }} />
        <h3 style={{ fontSize: "1rem", fontWeight: "600", color: "var(--octopus-ice)", margin: 0 }}>
          Delete by Supplier / Month
        </h3>
      </div>
      <p style={{ fontSize: "0.8rem", color: "var(--octopus-voltage)", opacity: 0.75, marginBottom: "1rem" }}>
        Pick any combination below to target a specific batch of invoices. Leaving a field blank means "all values".
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.75rem", marginBottom: "1rem" }}>
        <div>
          <label className="form-label" style={{ fontSize: "0.75rem", color: "var(--octopus-voltage)", opacity: 0.85 }}>Supplier</label>
          <Select value={supplier || "__any__"} onValueChange={v => setSupplier(v === "__any__" ? "" : v)}>
            <SelectTrigger className="form-input" data-testid="filter-delete-supplier">
              <SelectValue placeholder="Any supplier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Any supplier</SelectItem>
              {suppliers.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="form-label" style={{ fontSize: "0.75rem", color: "var(--octopus-voltage)", opacity: 0.85 }}>Month (YYYY-MM)</label>
          <Select
            value={month || "__any__"}
            onValueChange={v => { setMonth(v === "__any__" ? "" : v); if (v !== "__any__") setYear(""); }}
          >
            <SelectTrigger className="form-input" data-testid="filter-delete-month">
              <SelectValue placeholder="Any month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Any month</SelectItem>
              {months.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="form-label" style={{ fontSize: "0.75rem", color: "var(--octopus-voltage)", opacity: 0.85 }}>
            Year {month && <span style={{ opacity: 0.6 }}>(month takes precedence)</span>}
          </label>
          <Select
            value={year || "__any__"}
            onValueChange={v => setYear(v === "__any__" ? "" : v)}
            disabled={Boolean(month)}
          >
            <SelectTrigger className="form-input" data-testid="filter-delete-year">
              <SelectValue placeholder="Any year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Any year</SelectItem>
              {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
        <div data-testid="filter-delete-preview" style={{ fontSize: "0.9rem", color: "var(--octopus-ice)" }}>
          {!hasAnyFilter ? (
            <span style={{ opacity: 0.6 }}>Select at least one filter to preview the count.</span>
          ) : previewing ? (
            <span style={{ opacity: 0.8 }}><Loader2 size={14} className="animate-spin" style={{ display: "inline", marginRight: "0.5rem" }} />Counting…</span>
          ) : preview === null ? (
            <span style={{ opacity: 0.6 }}>—</span>
          ) : (
            <span>
              <strong style={{ color: previewColor, fontSize: "1.1rem" }}>{preview.toLocaleString()}</strong>{" "}
              invoice{preview === 1 ? "" : "s"} match this filter
            </span>
          )}
        </div>
        <Button
          variant="destructive"
          className="btn btn-danger"
          disabled={!hasAnyFilter || previewing || preview === 0 || preview === null}
          onClick={() => setConfirmOpen(true)}
          data-testid="filter-delete-open-confirm-btn"
        >
          Delete {preview > 0 ? preview.toLocaleString() : ""} Invoice{preview === 1 ? "" : "s"}
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="filter-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {preview?.toLocaleString() || 0} invoice{preview === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all invoices where <strong>{filterSummary}</strong>.
              Dashboard totals will recalculate automatically. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="filter-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="btn btn-danger"
              data-testid="filter-delete-confirm-btn"
            >
              {deleting ? <><Loader2 size={16} className="animate-spin" style={{ marginRight: "0.5rem" }} />Deleting…</> : "Yes, Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
